
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-form-error-display',
  imports: [],
  templateUrl: './form-error-display.component.html',
  styleUrl: './form-error-display.component.scss',
})
export class FormErrorDisplayComponent {
  @Input() form: FormGroup | null = null;
  @ViewChild('errorContainer') errorContainer!: ElementRef<HTMLDivElement>;

  private hasScrolled = false;

  ngAfterViewChecked(): void {
    if (!this.hasScrolled && this.shouldScroll()) {
      this.hasScrolled = true;
      setTimeout(() => {
        this.errorContainer?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    }
  }

  private shouldScroll(): boolean {
    if (!this.form) return false;

    return Object.values(this.form.controls).some((ctrl) => ctrl.invalid && ctrl.touched);
  }

  controlNames(formGroup: FormGroup | any = this.form, prefix = ''): string[] {
    if (!formGroup) return [];

    return Object.keys(formGroup.controls).flatMap((key) => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup) {
        return this.controlNames(control, prefix ? `${prefix}.${key}` : key);
      } else if (control instanceof FormArray) {
        return control.controls.flatMap((ctrl, i) =>
          ctrl instanceof FormGroup
            ? this.controlNames(ctrl, `${prefix ? prefix + '.' : ''}${key}.${i}`)
            : [`${prefix ? prefix + '.' : ''}${key}.${i}`]
        );
      } else {
        return [`${prefix ? prefix + '.' : ''}${key}`];
      }
    });
  }

  errorKeys(control: AbstractControl | null): string[] {
    return control && control.errors ? Object.keys(control.errors) : [];
  }

  getMessage(error: string, errorData: any): string {
    const messages: Record<string, string> = {
      required: 'Ce champ est requis.',
      min: `Valeur minimale : ${errorData?.min}`,
      maxlength: `Maximum ${errorData?.requiredLength} caractères`,
      duplicateColor: 'La couleur est déjà utilisée pour une autre variante',
    };
    return messages[error] || 'Champ invalide';
  }

  hasErrors(): boolean {
    return this.controlNames().some((name) => {
      const control = this.form?.get(name);
      return control?.invalid && control?.touched;
    });
  }

  humanize(controlName: string): string {
    return controlName
      .split('.')
      .map((part, index) => {
        const num = Number(part);
        if (!isNaN(num)) {
          return `#${num + 1}`;
        }
        return this.formatField(part);
      })
      .join(' - ');
  }

  private formatField(field: string): string {
    return field
      .replace(/[_\-]/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (str) => str.toUpperCase());
  }
}
